import { unref, computed, Ref, shallowRef } from 'vue'
import {
  isUndefined,
  updateValue,
  makeExternalValue,
  makeOptions,
  ItemValue,
  ItemsMap,
  KeyGetter,
  Value as Needle,
  Key,
} from '@/packages/selection-controller/general/main'

export type State<T = ItemValue, R = true> = {
  /** Boolean that represents whether "all" existing elements selected or not. */
  all: R extends true ? Ref<boolean> : boolean
  /** Included/selected elements. Will be empty if `all` is true. */
  included: R extends true ? Ref<T> : T
  /** Elements excluded from selection. Will be empty of `all` is false. */
  excluded: R extends true ? Ref<T> : T
}
export type AllControllerProps<T = ItemValue> = {
  /** Initial state. Resolves only the first time. */
  initial?: State<T[], false>
  /** Total amount of elements. */
  total: number
  /** Items key resolver. */
  keyGetter: KeyGetter
  /** Handler for update. */
  updateHandler: typeof updateValue
  /** Optional handler for state change notification. */
  onChange?: (state: State<ItemsMap<T>>) => any
}
export type AllController<T = ItemValue> = {
  /** Current state. */
  state: State<ItemsMap<T>>
  /** Update handler. */
  update: (needle: Needle<T>) => State<ItemsMap<T>>
  /** Amount of selected items. */
  amount: Ref<number>
  /** Set value of "all" selection boolean. */
  setAll: (value: boolean) => State<ItemsMap<T>>
  /** Method to check whether the key is selected right now. */
  isSelected: (key: Key) => boolean
  /** Method to notify about state change. Used internally in `update` and `setAll` methods. */
  notifyChange: () => State<ItemsMap<T>>
}
export type ListedControllerProps<T = ItemValue> = AllControllerProps<T> & {
  /** Currently visible items that can be managed. */
  options?: ItemValue[]
}
export type ListedController<T = ItemValue> = AllController<T> & {
  /** Little bit transformed current options with added `key`. */
  options: Ref<ItemsMap>
  /** Amount of options that are currently selected. */
  amountListed: Ref<number>
  toggleAllListed: () => State<ItemsMap<T>>
}
const u = unref
const v = <T = any> (item: Ref<T>): T => item.value
const MULTIPLE = true

/**
 * Hook that creates reactive "all" selection controller.
 *
 * Using this hook any items lists/partial items list can become "all"
 * selectable.
 *
 * As example: We can see paged result, but can toggle to select "all"
 * possibly existing items and exclude them if we need.
 *
 * @category General
 * @export
 * @template T - Atomic value, that controller is going to operate.
 * @param props - Reactive data and parameters to operate.
 * @returns Controller with ready to use data.
 */
export function useAll<T = ItemValue>(props: AllControllerProps<T>): AllController<T> {
  const initial = u(props.initial)
  const initialRef = (value?: T[]) => shallowRef<ItemsMap<T>>(makeOptions<T>(
    u(props.keyGetter), value || []
  ))
  const all = shallowRef<boolean>(u(initial?.all) || false)
  const included = initialRef(u(initial?.included))
  const excluded = initialRef(u(initial?.excluded))
  const isSelected = (key: Key): boolean => (
    v(all) ? isUndefined(v(excluded).get(key)) : !isUndefined(v(included).get(key))
  )
  const amount = computed<number>(() => (
    v(all) ? u(props.total) - v(excluded).items.length : v(included).items.length
  ))
  const state = { all, included, excluded }
  const notifyChange = () => {
    const val = { ...state }
    const onChange = u(props.onChange)

    if (!isUndefined(onChange)) {
      onChange(val)
    }

    return val
  }

  const update = (needle: Needle<T>) => {
    const val = v(all) ? excluded : included
    const keyGetter = u(props.keyGetter)

    val.value = makeOptions<T>(
      keyGetter,
      makeExternalValue(
        MULTIPLE,
        u(props.updateHandler)<T>(keyGetter, MULTIPLE, v(val), needle)
      )
    )

    return notifyChange()
  }

  const setAll = (val: boolean) => {
    all.value = val
    included.value = new ItemsMap<T>([])
    excluded.value = new ItemsMap<T>([])

    return notifyChange()
  }

  return {
    state,
    update,
    setAll,
    amount,
    isSelected,
    notifyChange,
  }
}

/**
 * Implements "Select All" tables(but any data actually) mechanics.
 *
 * Adds some additional methods to `useAll` controller for more easy
 * selection control.
 *
 * @category General
 * @export
 * @template T - Atomic value, that controller is going to operate.
 * @param props - Reactive data and parameters to operate.
 * @returns Controller with ready to use data.
 */
export function useAllListed<T = ItemValue>(props: ListedControllerProps<T>): ListedController<T> {
  const allController = useAll<T>(props)
  const { isSelected, update } = allController
  const options = computed<ItemsMap<T>>(() => makeOptions(
    u(props.keyGetter), u(props.options) || []
  ))
  const amountListed = computed<number>(() => v(options).items.reduce(
    (acc, { key }) => acc + (isSelected(key) as unknown as number), 0
  ))
  const toggleAllListed = () => {
    return update(
      v(amountListed) === v(options).items.length
        ? []
        : u(props.options) || []
    )
  }

  return {
    ...allController,
    options,
    amountListed,
    toggleAllListed,
  }
}
